<template xmlns="http://www.w3.org/1999/html">
    <div>
        <h2>{{ step.title }}</h2>
        <p class="step__notice">
            All fields with an asterisk are required
            <i class="fas fa-asterisk"></i>
        </p>

        <InputText
            name="national_insurance"
            v-model="formRef.national_insurance"
            :required="!formRef.national_insurance_dont_know"
            :alternative_option="true"
            aria_label="Field is required unless 'I don't know' option is selected"
            :error="formRef.errors.get('national_insurance')"
            @input="clearNationalInsuranceCheckbox"
        >
            <template v-slot:question>
                {{ getField("national_insurance").question }}
            </template>

            <template v-slot:input>
                <div class="form-group">Or</div>
                <div class="form-group d-flex align-items-center">
                    <input
                        type="checkbox"
                        id="national_insurance_dont_know"
                        v-model="formRef.national_insurance_dont_know"
                    />
                    <label for="national_insurance_dont_know" class="m-0 ms-2 form-label">
                        {{ getField("national_insurance_dont_know").label }}
                    </label>
                </div>
            </template>
        </InputText>

        <InputText
            v-if="props.previousStepFormData.where_complete_education === 'Scotland'"
            name="scottish_candidate_number"
            v-model="formRef.scottish_candidate_number"
            @input="clearScottishInsuranceCheckbox"
            :required="!formRef.scottish_candidate_number_dont_know"
            :alternative_option="true"
            aria_label="Field is required unless 'I don't know' option is selected"
            :error="formRef.errors.get('scottish_candidate_number')"
        >
            <template v-slot:question>
                {{ getField("scottish_candidate_number").question }}
            </template>

            <template v-slot:input>
                <div class="form-group">Or</div>
                <div class="form-group d-flex align-items-center">
                    <input
                        type="checkbox"
                        id="scottish_candidate_number_dont_know"
                        v-model="formRef.scottish_candidate_number_dont_know"
                    />
                    <label for="scottish_candidate_number_dont_know" class="m-0 ms-2 form-label">
                        {{ getField("scottish_candidate_number_dont_know").label }}
                    </label>
                </div>
            </template>
        </InputText>

        <InputRadioGroup
            name="no_work_household"
            :required="checkIsRequired('no_work_household')"
            :error="formRef.errors.get('no_work_household')"
            v-model="formRef.no_work_household"
            :options="getField('no_work_household')['options']"
            @input="formRef.errors.clear('no_work_household')"
        >
            <template v-slot:question>
                {{ getField("no_work_household").question }}
            </template>

        </InputRadioGroup>

        <InputRadioGroup
            name="benefits"
            :required="checkIsRequired('benefits')"
            :error="formRef.errors.get('benefits')"
            v-model="formRef.benefits"
            :options="getField('benefits')['options']"
            @input="formRef.errors.clear('benefits')"
        >
            <template v-slot:question>
                {{ getField("benefits").question }}
            </template>

        </InputRadioGroup>

        <InputCheckboxGroup
            v-if="formRef.benefits == 'yes'"
            name="which_benefits"
            :required="checkIsRequired('which_benefits')"
            :error="formRef.errors.get('which_benefits')"
            v-model="formRef.which_benefits"
            :form="formRef"
            :options="getField('which_benefits')['options']"
            @update:modelValue="handleBenefitsInput"
        >
            <template v-slot:question>
                {{ getField("which_benefits").question }}
            </template>

        </InputCheckboxGroup>

        <InputYearsMonths
            v-if="formRef.benefits == 'yes' && (formRef.which_benefits && formRef.which_benefits.includes('job_seekers_allowance'))"
            name="job_seekers_allowance"
            :required="!formRef.job_seekers_allowance_months_prefer_not_to_say"
            :alternative_option="true"
            aria_label="Field is required unless 'prefer not to say' option is selected"
            :error="formRef.errors.get('job_seekers_allowance_months') || formRef.errors.get('job_seekers_allowance_years')"
            v-model:years="formRef.job_seekers_allowance_years"
            v-model:months="formRef.job_seekers_allowance_months"
            :form="formRef"
        >
            <template v-slot:question>
                {{ getField("job_seekers_allowance_months").question }}
            </template>

            <template v-slot:input>
                <div class="form-group">Or</div>
                <div class="form-group d-flex align-items-center">
                    <input
                        type="checkbox"
                        id="job_seekers_allowance_months_prefer_not_to_say"
                        v-model="formRef.job_seekers_allowance_months_prefer_not_to_say"
                        @change="clearYearsMonthsAnswer($event, ['job_seekers_allowance_months', 'job_seekers_allowance_years'])"
                    />
                    <label for="job_seekers_allowance_months_prefer_not_to_say" class="m-0 ms-2 form-label">
                        Prefer not to say
                    </label>
                </div>
            </template>
        </InputYearsMonths>

        <InputYearsMonths
            v-if="formRef.benefits == 'yes' && (formRef.which_benefits && formRef.which_benefits.includes('universal_credit'))"
            name="universal_credit"
            :error="formRef.errors.get('universal_credit_months') || formRef.errors.get('universal_credit_years')"
            v-model:years="formRef.universal_credit_years"
            v-model:months="formRef.universal_credit_months"
            :form="formRef"
            :required="!formRef.universal_credit_months_prefer_not_to_say"
            :alternative_option="true"
            aria_label="Field is required unless 'prefer not to say' option is selected"
        >
            <template v-slot:question>
                {{ getField("universal_credit_months").question }}
            </template>

            <template v-slot:input>
                <div class="form-group">Or</div>
                <div class="form-group d-flex align-items-center">
                    <input
                        type="checkbox"
                        id="universal_credit_months_prefer_not_to_say"
                        v-model="formRef.universal_credit_months_prefer_not_to_say"
                    />
                    <label for="universal_credit_months_prefer_not_to_say" class="m-0 ms-2 form-label">
                        Prefer not to say
                    </label>
                </div>
            </template>
        </InputYearsMonths>

        <InputCheckboxGroup
            v-if="formRef.which_benefits && formRef.which_benefits.includes('universal_credit')"
            name="universal_credit_opts"
            :required="true"
            :error="formRef.errors.get('universal_credit_opts')"
            v-model="formRef.universal_credit_opts"
            :form="formRef"
            :options="getField('universal_credit_opts')['options']"
            @update:modelValue="handleCreditsInput"
        >
            <template v-slot:question>
                {{ getField("universal_credit_opts").question }}
            </template>

        </InputCheckboxGroup>

        <InputRadioGroup
            v-if="formRef.which_benefits && formRef.which_benefits.includes('employment_support_allowance')"
            name="work_related_activity_group"
            :required="true"
            :error="formRef.errors.get('work_related_activity_group')"
            v-model="formRef.work_related_activity_group"
            :options="getField('work_related_activity_group')['options']"
            @input="formRef.errors.clear('work_related_activity_group')"
        >
            <template v-slot:question>
                {{ getField("work_related_activity_group").question }}
            </template>

        </InputRadioGroup>

        <InputCheckboxGroup
            name="allowances"
            :required="checkIsRequired('allowances')"
            :error="formRef.errors.get('allowances')"
            v-model="formRef.allowances"
            :form="formRef"
            :options="getField('allowances')['options']"
            @update:modelValue="handleAllowancesInput"
        >
            <template v-slot:question>
                {{ getField("allowances").question }}
            </template>

        </InputCheckboxGroup>
    </div>
</template>

<script setup>
import InputRadioGroup from "../Form/InputRadioGroup";
import InputCheckboxGroup from "../../components/Programmes/GetInto/Form/InputCheckboxGroup.vue"
import InputYearsMonths from "../Form/InputYearsMonths";
import InputText from "../Form/InputText";
import Form from "../../Form";
import { isRequired } from "./helpers";
import { ref, markRaw, watch } from "vue";

const props = defineProps({
    step: Object,
    fields: Object,
    formData: Object,
    previousStepFormData: Object
})

let formRef = markRaw(ref(new Form({
    national_insurance: null,
    national_insurance_dont_know: null,
    scottish_candidate_number: null,
    scottish_candidate_number_dont_know: !props.previousStepFormData?.highest_qualification_scotland,
    no_work_household: null,
    benefits: null,
    which_benefits: null,
    job_seekers_allowance_months_prefer_not_to_say: null,
    job_seekers_allowance_months: null,
    job_seekers_allowance_years: null,
    universal_credit_years: null,
    universal_credit_months: null,
    universal_credit_months_prefer_not_to_say: null,
    universal_credit_opts: null,
    work_related_activity_group: null,
    allowances: null
}, props.formData)))

const getField = (fieldName) => {
   return props.fields[fieldName];
}

const checkIsRequired = (fieldName) => {
   var fieldData = getField(fieldName);
   return isRequired(fieldData, formRef.value);
}

function handleAllowancesInput(values) {
    formRef.value.errors.clear('allowances');
    if (values.length && (values[values.length - 1] === 'none_of_these' || values[values.length - 1] === 'prefer_not_to_say')) {
        formRef.value.allowances = [values[values.length - 1]];
        return;
    }
    clearCheckboxValues(['none_of_these', 'prefer_not_to_say'], values, 'allowances');
}

function handleBenefitsInput(values) {
    formRef.value.errors.clear('which_benefits');
    if (values.length && (values[values.length - 1] === 'benefits_none' || values[values.length - 1] === 'benefits_dont_know')) {
        formRef.value.which_benefits = [values[values.length - 1]];
        return;
    }
    clearCheckboxValues(['benefits_none', 'benefits_dont_know'], values, 'which_benefits');
}
       
function handleCreditsInput(values) {
    formRef.value.errors.clear('universal_credit_opts');
    if (values.length && (values[values.length - 1] === 'none_of_these' || values[values.length - 1] === 'prefer_not_to_say')) {
        formRef.value.universal_credit_opts = [values[values.length - 1]];
        return;
    }
    clearCheckboxValues(['none_of_these', 'prefer_not_to_say'], values, 'universal_credit_opts');
}

watch(() => formRef.value.national_insurance_dont_know, value => {
    if (value) {
        formRef.value.national_insurance = null
 }
})

watch(() => formRef.value.scottish_candidate_number_dont_know, value => {
    if (value) {
        formRef.value.scottish_candidate_number = null
 }
})

watch(() => formRef.value.universal_credit_months_prefer_not_to_say, value => {
    if (value) {
        formRef.value.universal_credit_years = null
        formRef.value.universal_credit_months = null
 }
})

watch(() => formRef.value.job_seekers_allowance_months_prefer_not_to_say, value => {
    if (value) {
        formRef.value.job_seekers_allowance_years = null
        formRef.value.job_seekers_allowance_months = null
 }
})

function clearNationalInsuranceCheckbox() {
    formRef.value.national_insurance_dont_know = false;
}

function clearScottishInsuranceCheckbox() {
    formRef.value.scottish_candidate_number_dont_know = false;
}

function clearYearsMonthsAnswer(checked, values) {
    if (checked.target.checked === true) {
        values.forEach(function(value) {
            formRef.value[value] = null;
        });
    }
}

function clearCheckboxValues(previousAnswers, values, formField) { 
    for (const answer of previousAnswers) {
        if (values.includes(answer)) {
            formRef.value[formField] = values.filter(value => value !== answer);
        }
    }
}

defineExpose({
 formRef
})
</script>
